import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardList, Plus, Send, Trash2, Unlock } from 'lucide-react';
import { canReopenQuestionnaire } from '@peles/shared';
import type { QuestionnaireListItem, QuestionnaireStatus } from '@peles/shared';
import { EntityList, StatusBadge } from '../components/common';
import type { EntityListColumn } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import { SearchableListSelect } from '../components/common/SearchableListSelect';
import { ReopenQuestionnaireModal, SendQuestionnaireModal } from '../components/questionnaires';
import {
  useCloneQuestionnaire,
  useDeleteQuestionnaire,
  useQuestionnaires,
} from '../hooks/useQuestionnaires';
import { useEvent } from '../hooks/useEvents';
import { useDialog } from '../context/DialogContext';

const STATUS_COLORS: Record<QuestionnaireStatus, string> = {
  DRAFT: 'gray',
  SENT: 'blue',
  IN_PROGRESS: 'amber',
  SUBMITTED: 'green',
  REVIEWED: 'purple',
};

/** Per-event questionnaires: attach from a template, send to client, review. */
export function QuestionnairesPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { data: event } = useEvent(eventId || '');
  const { data: questionnaires = [], isLoading } = useQuestionnaires(
    eventId ? { eventId } : undefined,
  );
  const [showAttach, setShowAttach] = useState(false);
  const [sendTarget, setSendTarget] = useState<QuestionnaireListItem | null>(null);
  const [reopenTarget, setReopenTarget] = useState<QuestionnaireListItem | null>(null);
  const deleteQuestionnaire = useDeleteQuestionnaire();

  const columns: EntityListColumn<QuestionnaireListItem>[] = [
    {
      key: 'title',
      header: 'Questionnaire',
      render: (q) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-100">
            <ClipboardList size={16} className="text-amber-600" />
          </div>
          <div>
            <p className="text-gray-900 font-semibold">{q.title}</p>
            {q.sentTo && <p className="text-xs text-gray-500">Sent to {q.sentTo}</p>}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (q) => (
        <StatusBadge label={q.status.replace('_', ' ')} color={STATUS_COLORS[q.status]} />
      ),
      width: 'w-28',
    },
    {
      key: 'progress',
      header: 'Answered',
      render: (q) => (
        <span className="text-sm text-gray-600">
          {q.answeredCount}/{q.questionCount}
        </span>
      ),
      width: 'w-24',
    },
    {
      key: 'submitted',
      header: 'Submitted',
      render: (q) => (
        <span className="text-xs text-gray-500">
          {q.submittedAt
            ? new Date(q.submittedAt).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })
            : '—'}
        </span>
      ),
      width: 'w-28',
      hideOnMobile: true,
    },
    {
      key: 'actions',
      header: '',
      render: (q) => (
        <div className="flex items-center gap-1 justify-end">
          {canReopenQuestionnaire(q) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setReopenTarget(q);
              }}
              className="p-1.5 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-amber-50"
              aria-label="Reopen questionnaire"
              data-testid={`reopen-${q.id}`}
            >
              <Unlock size={14} />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSendTarget(q);
            }}
            className="p-1.5 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-gray-50"
            aria-label="Send to client"
            data-testid={`send-${q.id}`}
          >
            <Send size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              void (async () => {
                if (
                  await confirm({
                    title: 'Delete Questionnaire',
                    message: `Delete "${q.title}" and all its answers?`,
                    confirmLabel: 'Delete',
                  })
                ) {
                  deleteQuestionnaire.mutate(q.id);
                }
              })();
            }}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50"
            aria-label="Delete questionnaire"
          >
            <Trash2 size={14} />
          </button>
        </div>
      ),
      width: 'w-28',
    },
  ];

  return (
    <div>
      <button
        onClick={() => navigate(`/events/${eventId}`)}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeft size={16} />
        {event?.name ?? 'Event'}
      </button>
      <EntityList
        items={questionnaires}
        columns={columns}
        getKey={(q) => q.id}
        isLoading={isLoading}
        onRowClick={(q) => navigate(`/events/${eventId}/questionnaires/${q.id}`)}
        title="Questionnaires"
        titleActions={
          <button
            onClick={() => setShowAttach(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            data-testid="add-questionnaire-button"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Add Questionnaire</span>
          </button>
        }
        emptyMessage="No questionnaires yet. Add one from a template to send to your client."
      />

      <AttachTemplateModal
        open={showAttach}
        onClose={() => setShowAttach(false)}
        eventId={eventId!}
        onCreated={(id) => {
          setShowAttach(false);
          navigate(`/events/${eventId}/questionnaires/${id}`);
        }}
      />

      <SendQuestionnaireModal
        open={sendTarget !== null}
        onClose={() => setSendTarget(null)}
        questionnaireId={sendTarget?.id ?? null}
        sentTo={sendTarget?.sentTo}
      />

      <ReopenQuestionnaireModal
        open={reopenTarget !== null}
        onClose={() => setReopenTarget(null)}
        questionnaireId={reopenTarget?.id ?? null}
        sentTo={reopenTarget?.sentTo}
      />
    </div>
  );
}

function AttachTemplateModal({
  open,
  onClose,
  eventId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string;
  onCreated: (questionnaireId: string) => void;
}) {
  const { data: templates = [], isLoading } = useQuestionnaires({ isTemplate: true });
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const cloneQuestionnaire = useCloneQuestionnaire();

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!templateId) return;
    cloneQuestionnaire.mutate(
      { id: templateId, eventId, title: title.trim() || undefined },
      { onSuccess: (created) => onCreated(created.id) },
    );
  }

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Add Questionnaire"
      size="sm"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="attach-template-form"
            disabled={!templateId || cloneQuestionnaire.isPending}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
            data-testid="attach-template-submit"
          >
            {cloneQuestionnaire.isPending ? 'Creating…' : 'Create from Template'}
          </button>
        </div>
      }
    >
      <form id="attach-template-form" onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Questions linked to event data (date, venue, timeline times, tags) are prefilled
          automatically.
        </p>
        <div data-testid="attach-template-select">
          <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
          <SearchableListSelect
            items={templates}
            value={templateId || null}
            onChange={(value) => setTemplateId(typeof value === 'string' ? value : '')}
            getValue={(template) => template.id}
            getLabel={(template) => template.templateName ?? template.title}
            isLoading={isLoading}
            placeholder="Choose a template…"
            ariaLabel="Template"
            emptyMessage="No questionnaire templates yet."
          />
        </div>
        <div>
          <label htmlFor="attach-title" className="block text-sm font-medium text-gray-700 mb-1">
            Title (optional — defaults to the template title)
          </label>
          <input
            id="attach-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </form>
    </EntityModal>
  );
}
